import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  Stack,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  YouTube as YouTubeIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

const XIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const GoogleIcon = () => ( /* Your Google SVG icon code remains the same */
    <svg width="20" height="20" viewBox="0 0 48 48">
        <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"></path><path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path><path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.222,0-9.617-3.21-11.28-7.581l-6.522,5.025C9.505,39.556,16.227,44,24,44z"></path><path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.574l6.19,5.238C42.718,36.319,44,32.1,44,28C44,25.441,43.862,22.771,43.611,20.083z"></path>
    </svg>
);

// NOTE: removed TypeScript `interface Platform` to keep this file valid JSX.
// platforms is a plain JS array of objects.
const platforms = [
  {
    id: 'facebook',
    name: 'Facebook',
    icon: FacebookIcon,
    color: '#1877F2',
    bgColor: '#F0F6FF',
    gradient: 'linear-gradient(135deg, #1877F2 0%, #0866ff 100%)',
    description: 'Connect your Facebook account to manage posts and pages',
    fields: [
      { name: 'client_id', label: 'Client ID', type: 'text', required: true, placeholder: 'Enter Facebook client ID' },
      { name: 'client_secret', label: 'Client Secret', type: 'text', required: false, placeholder: 'Enter Facebook client Secret' },
      { name: 'scopes', label: 'Scopes', type: 'text', required: false, placeholder: 'Enter Scopes' }
    ]
  },
  {
    id: 'instagram',
    name: 'Instagram',
    icon: InstagramIcon,
    color: '#E4405F',
    bgColor: '#FEF7F7',
    gradient: 'linear-gradient(45deg, #feda75 0%, #fa7e1e 25%, #d62976 50%, #962fbf 75%, #4f5bd5 100%)',
    description: 'Connect your Instagram business account',
    fields: [
      { name: 'access_token', label: 'Access Token', type: 'text', required: true, placeholder: 'Enter Instagram access token' },
      { name: 'account_id', label: 'Account ID', type: 'text', required: true, placeholder: 'Instagram business account ID' }
    ]
  },
  {
    id: 'google',
    name: 'Google',
    icon: GoogleIcon, 
    color: '#4285F4',      
    bgColor: 'linear-gradient(90deg, #EA4335 0%, #EA4335 25%, #FBBC05 25%, #FBBC05 50%, #34A853 50%, #34A853 75%, #4285F4 75%, #4285F4 100%)',    // Background can remain light for contrast
    gradient: 'linear-gradient(135deg, #ffffffff 0%, #ffffffff 50%, #fdfdfdff 75%, #ffffffff 100%)',
    description: 'Connect your Google Account',
    fields: [
      { name: 'redirect_uri_template', label: 'Redirect URL', type: 'text', required: true, placeholder: 'Enter Google Redirect URL' },
      { name: 'client_id', label: 'Client ID', type: 'text', required: true, placeholder: 'Enter Facebook client ID' },
      { name: 'client_secret', label: 'Client Secret', type: 'text', required: false, placeholder: 'Enter Facebook client Secret' },
      { name: 'scopes', label: 'Scopes', type: 'text', required: false, placeholder: 'Enter Scopes' }
    ]
  },
  {
    id: 'linkedin',
    name: 'LinkedIn',
    icon: LinkedInIcon,
    color: '#0077B5',
    bgColor: '#F0F8FF',
    gradient: 'linear-gradient(135deg, #0077B5 0%, #005582 100%)',
    description: 'Connect your LinkedIn profile or company page',
    fields: [
      { name: 'access_token', label: 'Access Token', type: 'text', required: true, placeholder: 'Enter LinkedIn access token' },
      { name: 'user_id', label: 'User ID', type: 'text', required: false, placeholder: 'Optional: User ID' },
      { name: 'organization_id', label: 'Organization ID', type: 'text', required: false, placeholder: 'Optional: Company page ID' }
    ]
  },
  {
    id: 'twitter',
    name: 'Twitter (X)',
    icon: XIcon,
    color: '#000000',
    bgColor: '#F8F8F8',
    gradient: 'linear-gradient(135deg, #080808ff 0%, #070707ff 100%)',
    description: 'Connect your Twitter/X account',
    fields: [
      { name: 'access_token', label: 'Access Token', type: 'text', required: true, placeholder: 'Enter Twitter access token' },
      { name: 'access_token_secret', label: 'Access Token Secret', type: 'password', required: true, placeholder: 'Enter access token secret' },
      { name: 'api_key', label: 'API Key', type: 'text', required: false, placeholder: 'Optional: API Key' },
      { name: 'api_secret', label: 'API Secret', type: 'password', required: false, placeholder: 'Optional: API Secret' }
    ]
  }
];

export default function ApiIntegration() {
  // removed TypeScript generics, use plain JS states
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleOpenDialog = (platform) => {
    setSelectedPlatform(platform);
    setFormData({});
    setError(null);
    setSuccess(null);
  };

  const handleCloseDialog = () => {
    setSelectedPlatform(null);
    setFormData({});
    setError(null);
    setSuccess(null);
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleSubmit = async () => {
    if (!selectedPlatform) return;

    const requiredFields = selectedPlatform.fields.filter(f => f.required);
    const missingFields = requiredFields.filter(f => !formData[f.name]?.trim());

    if (missingFields.length > 0) {
      setError(`Please fill in all required fields: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');

      const payload = {
        platform: selectedPlatform.id,
        platformType: selectedPlatform.id.toUpperCase(),
        ...formData
      };

      const response = await fetch('https://socialmedia.gtlit.com/api/add_platform', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to connect ${selectedPlatform.name}`);
      }

      await response.json();
      setSuccess(`${selectedPlatform.name} connected successfully!`);

      setTimeout(() => {
        handleCloseDialog();
      }, 2000);
    } catch (err) {
      setError(err.message || 'An error occurred while connecting the platform');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        p: { xs: 2, md: 4 },
        background: '#f4f1ff'
      }}
    >
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: '800',
            background: 'linear-gradient(90deg,rgba(2, 0, 36, 1) 0%, rgba(9, 9, 121, 1) 35%, rgba(0, 212, 255, 1) 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1
          }}
        >
          API Integration
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: 'black',
            mb: 3,
            fontWeight: 700
          }}
        >
          Connect your social media platforms
        </Typography>
      </Box>

      <Grid container spacing={4} sx={{ maxWidth: 1400, mx: 'auto' }}>
        {platforms.map((platform) => {
          const IconComponent = platform.icon;

          return (
            <Grid item xs={12} sm={6} md={4} key={platform.id}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 20px 60px ${platform.color}20`,
                    '& .platform-avatar': {
                      transform: 'scale(1.1)',
                      boxShadow: `0 8px 32px ${platform.color}40`
                    }
                  }
                }}
                onClick={() => handleOpenDialog(platform)}
              >
                <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <Avatar
                      className="platform-avatar"
                      sx={{
                        background: platform.gradient,
                        width: 56,
                        height: 56,
                        transition: 'all 0.3s ease',
                        boxShadow: `0 4px 20px ${platform.color}30`
                      }}
                    >
                      <IconComponent sx={{ color: 'white', fontSize: 28 }} />
                    </Avatar>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
                      {platform.name}
                    </Typography>
                  </Stack>

                  <Typography
                    variant="body2"
                    sx={{
                      color: '#64748b',
                      mb: 3,
                      flex: 1,
                      lineHeight: 1.6
                    }}
                  >
                    {platform.description}
                  </Typography>

                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<AddIcon />}
                    sx={{
                      borderRadius: 3,
                      textTransform: 'none',
                      py: 1.5,
                      fontWeight: 700,
                      background: platform.id === "google" ? platform.bgColor : platform.gradient,
                      '&:hover': {
                        transform: 'scale(1.02)',
                        boxShadow: `0 8px 24px ${platform.color}30`
                      }
                    }}
                  >
                    Connect
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Dialog
        open={!!selectedPlatform}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 32px 64px rgba(0,0,0,0.2)'
          }
        }}
      >
        {selectedPlatform && (
          <>
            <DialogTitle sx={{ pb: 2, pt: 3 }}>
              <Stack direction="row" alignItems="center" spacing={3}>
                <Avatar
                  sx={{
                    background: selectedPlatform.gradient,
                    width: 56,
                    height: 56,
                    boxShadow: `0 4px 20px ${selectedPlatform.color}40`
                  }}
                >
                  {/* icon component could be a React component or a function returning svg */}
                  {typeof selectedPlatform.icon === 'function'
                    ? React.createElement(selectedPlatform.icon, { sx: { color: 'white', fontSize: 28 } })
                    : null}
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Connect {selectedPlatform.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Enter your API credentials
                  </Typography>
                </Box>
              </Stack>
            </DialogTitle>

            <DialogContent sx={{ pt: 3, pb: 2 }}>
              <Stack spacing={3}>
                {error && (
                  <Alert severity="error" sx={{ borderRadius: 2 }}>
                    {error}
                  </Alert>
                )}

                {success && (
                  <Alert
                    severity="success"
                    sx={{ borderRadius: 2 }}
                  >
                    {success}
                  </Alert>
                )}

                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  <Typography variant="body2">
                    Your credentials are encrypted and securely stored. Required fields are marked with *.
                  </Typography>
                </Alert>

                {selectedPlatform.fields.map((field) => (
                  <TextField
                    key={field.name}
                    fullWidth
                    label={field.label}
                    type={field.type}
                    required={field.required}
                    placeholder={field.placeholder}
                    value={formData[field.name] || ''}
                    onChange={(e) => handleFieldChange(field.name, e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />
                ))}
              </Stack>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 2 }}>
              <Button
                onClick={handleCloseDialog}
                disabled={isSubmitting}
                sx={{ textTransform: 'none', fontWeight: 600, px: 3 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 4,
                  py: 1.2,
                  borderRadius: 2,
                  background: selectedPlatform.gradient,
                  '&:hover': {
                    transform: 'scale(1.02)'
                  }
                }}
              >
                {isSubmitting ? 'Connecting...' : 'Connect Account'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
